import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useTasks } from "../contexts/TaskContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sparkles,
  ListChecks,
  Search,
  Wand2,
  Clock,
  Repeat,
  ChevronRight,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { emitUpgradeRequired } from "../components/UpgradeRequiredDialog";
import { WORKSPACES } from "../lib/workspaces";

const PRIORITY_TINT = {
  urgent: "#EF4444",
  important: "#F59E0B",
  normal: "#0281FF",
  low: "#64748B",
};

function recLabel(rule) {
  if (!rule || !rule.freq) return "";
  const interval = rule.interval || 1;
  switch (rule.freq) {
    case "daily":
      return interval === 1 ? "Daily" : `Every ${interval} days`;
    case "weekdays":
      return "Weekdays";
    case "weekly":
      return interval === 1 ? "Weekly" : `Every ${interval} weeks`;
    case "monthly":
      return interval === 1 ? "Monthly" : `Every ${interval} months`;
    case "quarterly":
      return "Quarterly";
    case "yearly":
      return "Yearly";
    default:
      return rule.freq;
  }
}

export default function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIndustry, setActiveIndustry] = useState("all");
  const [search, setSearch] = useState("");
  const [previewTpl, setPreviewTpl] = useState(null);
  const [applyState, setApplyState] = useState(null); // { template, dashboard, start_date }
  const [applying, setApplying] = useState(false);
  const navigate = useNavigate();
  const { refresh: refreshTasks } = useTasks();

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [tplRes, catRes] = await Promise.all([
        api.get("/templates"),
        api.get("/templates/categories"),
      ]);
      setTemplates(tplRes.data?.templates || []);
      setCategories(catRes.data?.categories || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const visible = useMemo(() => {
    let list = templates;
    if (activeIndustry !== "all") {
      list = list.filter((t) => (t.industry || "general") === activeIndustry);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (t) =>
          t.name?.toLowerCase().includes(q) ||
          t.description?.toLowerCase().includes(q) ||
          t.category?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [templates, activeIndustry, search]);

  const counts = useMemo(() => {
    const c = { all: templates.length };
    for (const t of templates) {
      const key = t.industry || "general";
      c[key] = (c[key] || 0) + 1;
    }
    return c;
  }, [templates]);

  const onPreview = async (tpl) => {
    try {
      const { data } = await api.get(`/templates/${tpl.id}`);
      setPreviewTpl(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load template");
    }
  };

  const openApply = (tpl) => {
    const today = new Date().toISOString().slice(0, 10);
    setApplyState({
      template: tpl,
      dashboard: tpl.default_dashboard || "work",
      start_date: today,
    });
    setPreviewTpl(null);
  };

  const doApply = async () => {
    if (!applyState) return;
    setApplying(true);
    try {
      const { data } = await api.post(
        `/templates/${applyState.template.id}/apply`,
        {
          dashboard: applyState.dashboard,
          start_date: applyState.start_date,
        }
      );
      toast.success(
        `Added ${data.tasks.length} task${data.tasks.length === 1 ? "" : "s"} to ${data.dashboard}`
      );
      await refreshTasks();
      setApplyState(null);
      // Navigate to the target dashboard to see the tasks land
      navigate(`/${data.dashboard}`);
    } catch (e) {
      const status = e.response?.status;
      const detail = formatApiError(e.response?.data?.detail);
      if (status === 402) {
        emitUpgradeRequired({
          title: "Task limit reached",
          message:
            detail ||
            "This template would push you over the 50-task Free plan cap. Upgrade to Pro for unlimited tasks, or delete some existing ones first.",
          used: 50,
          limit: 50,
          unit: "tasks",
        });
      } else {
        toast.error(detail || "Failed to apply template");
      }
    } finally {
      setApplying(false);
    }
  };

  const onDeleteUserTpl = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/templates/${tpl.id}`);
      setTemplates((prev) => prev.filter((t) => t.id !== tpl.id));
      toast.success("Template deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div
      data-testid="templates-page"
      className="relative px-4 sm:px-8 py-8 max-w-[1600px] mx-auto"
    >
      {/* Ambient glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 opacity-60"
        style={{
          background:
            "radial-gradient(900px 320px at 100% -10%, rgba(249,117,34,0.12) 0%, transparent 60%)",
        }}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-7 anim-fade-up">
        <div className="flex items-start gap-4">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
            style={{
              backgroundColor: "rgba(249,117,34,0.12)",
              border: "1px solid rgba(249,117,34,0.35)",
              color: "#F97522",
            }}
          >
            <Sparkles size={26} strokeWidth={1.8} />
          </div>
          <div>
            <div
              className="text-[10px] uppercase tracking-[0.25em] mb-2 font-bold"
              style={{ color: "#F97522" }}
            >
              Templates
            </div>
            <h1
              data-testid="templates-page-title"
              className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-none"
            >
              SOP & Checklist Library
            </h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-xl">
              Pre-built operating procedures and routines for restaurants, offices, households &
              more. One click drops them straight into any workspace as ready-to-run tasks.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 mb-6">
        <div className="flex items-center gap-2 flex-wrap" data-testid="templates-filter-chips">
          {categories.map((cat) => {
            const active = activeIndustry === (cat.industry || cat.key);
            const key = cat.industry || cat.key;
            const color = cat.color || "#0281FF";
            const count = counts[key === "all" ? "all" : key] || 0;
            return (
              <button
                key={cat.key}
                onClick={() => setActiveIndustry(key)}
                data-testid={`templates-chip-${key}`}
                style={
                  active
                    ? {
                        backgroundColor: `${color}1A`,
                        borderColor: `${color}66`,
                        color: color,
                      }
                    : {}
                }
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border text-xs font-medium transition-all duration-200 ${
                  active ? "" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                }`}
              >
                {cat.icon && <span>{cat.icon}</span>}
                <span>{cat.label}</span>
                <span className="text-[10px] font-mono opacity-70">{count}</span>
              </button>
            );
          })}
        </div>

        <div className="relative max-w-sm">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            data-testid="templates-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search templates…"
            className="pl-9 h-9"
          />
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="text-sm text-muted-foreground py-20 text-center">Loading templates…</div>
      ) : visible.length === 0 ? (
        <div
          data-testid="templates-empty"
          className="text-sm text-muted-foreground py-20 text-center border border-dashed border-border rounded-xl"
        >
          No templates match your filters.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visible.map((tpl) => (
            <TemplateCard
              key={tpl.id}
              template={tpl}
              onPreview={onPreview}
              onApply={openApply}
              onDelete={onDeleteUserTpl}
            />
          ))}
        </div>
      )}

      {/* Preview Drawer (Dialog) */}
      <Dialog open={!!previewTpl} onOpenChange={(v) => !v && setPreviewTpl(null)}>
        <DialogContent
          data-testid="templates-preview-dialog"
          className="max-w-2xl max-h-[85vh] overflow-y-auto"
        >
          {previewTpl && (
            <>
              <DialogHeader>
                <div className="flex items-start gap-3 mb-2">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl shrink-0"
                    style={{
                      backgroundColor: `${previewTpl.color || "#0281FF"}1A`,
                      border: `1px solid ${previewTpl.color || "#0281FF"}40`,
                    }}
                  >
                    <span>{previewTpl.icon || "✅"}</span>
                  </div>
                  <div className="flex-1">
                    <DialogTitle className="font-display text-2xl font-black tracking-tight">
                      {previewTpl.name}
                    </DialogTitle>
                    <DialogDescription className="mt-1">
                      {previewTpl.description}
                    </DialogDescription>
                    <div className="flex flex-wrap items-center gap-2 mt-3 text-[11px] uppercase tracking-wider text-muted-foreground">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary">
                        <ListChecks size={11} /> {previewTpl.tasks?.length || 0} tasks
                      </span>
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
                        style={{
                          backgroundColor: `${previewTpl.color || "#0281FF"}1A`,
                          color: previewTpl.color || "#0281FF",
                        }}
                      >
                        {previewTpl.industry}
                      </span>
                      <span className="px-2 py-0.5 rounded-full bg-secondary">
                        {previewTpl.category}
                      </span>
                    </div>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-2 mt-2">
                {(previewTpl.tasks || []).map((t, idx) => (
                  <div
                    key={idx}
                    className="rounded-lg border border-border bg-secondary/30 p-3"
                    data-testid={`templates-preview-task-${idx}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 min-w-0">
                        <span
                          className="mt-1 inline-block w-1.5 h-1.5 rounded-full shrink-0"
                          style={{
                            backgroundColor:
                              PRIORITY_TINT[t.priority] || PRIORITY_TINT.normal,
                          }}
                        />
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">{t.title}</div>
                          {t.description && (
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {t.description}
                            </div>
                          )}
                          {t.subtasks?.length > 0 && (
                            <ul className="mt-2 ml-1 space-y-0.5 text-xs text-muted-foreground">
                              {t.subtasks.map((s, i) => (
                                <li key={i} className="flex items-center gap-1.5">
                                  <span className="inline-block w-1 h-1 rounded-full bg-muted-foreground" />
                                  {s.title}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        {t.due_time && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                            <Clock size={10} /> {t.due_time}
                          </span>
                        )}
                        {t.recurrence && (
                          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-border">
                            <Repeat size={9} /> {recLabel(t.recurrence)}
                          </span>
                        )}
                        {t.due_offset_days > 0 && (
                          <span className="text-[10px] text-muted-foreground">
                            +{t.due_offset_days}d
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <DialogFooter className="mt-4 flex-col sm:flex-row gap-2">
                <Button
                  variant="ghost"
                  onClick={() => setPreviewTpl(null)}
                  data-testid="templates-preview-close-btn"
                >
                  Close
                </Button>
                <Button
                  onClick={() => openApply(previewTpl)}
                  data-testid="templates-preview-apply-btn"
                  style={{
                    backgroundColor: previewTpl.color || "#0281FF",
                    color: "#fff",
                  }}
                  className="hover:opacity-90"
                >
                  <Wand2 size={14} className="mr-1.5" /> Apply to workspace
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Apply Dialog */}
      <Dialog open={!!applyState} onOpenChange={(v) => !v && setApplyState(null)}>
        <DialogContent data-testid="templates-apply-dialog" className="max-w-md">
          {applyState && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl font-black tracking-tight">
                  Apply "{applyState.template.name}"
                </DialogTitle>
                <DialogDescription>
                  Drop these {applyState.template.tasks?.length || 0} tasks into a workspace.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 mt-2">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Workspace
                  </Label>
                  <Select
                    value={applyState.dashboard}
                    onValueChange={(v) =>
                      setApplyState((s) => ({ ...s, dashboard: v }))
                    }
                  >
                    <SelectTrigger
                      className="mt-1.5"
                      data-testid="templates-apply-workspace-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(WORKSPACES).map((w) => (
                        <SelectItem key={w.key} value={w.key}>
                          {w.fullLabel}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Start date
                  </Label>
                  <Input
                    type="date"
                    value={applyState.start_date}
                    onChange={(e) =>
                      setApplyState((s) => ({ ...s, start_date: e.target.value }))
                    }
                    className="mt-1.5"
                    data-testid="templates-apply-start-date"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Each task's due date will be calculated from this date.
                  </p>
                </div>
              </div>

              <DialogFooter className="mt-4">
                <Button
                  variant="ghost"
                  onClick={() => setApplyState(null)}
                  data-testid="templates-apply-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={doApply}
                  disabled={applying}
                  data-testid="templates-apply-confirm-btn"
                >
                  {applying ? "Applying…" : "Apply template"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TemplateCard({ template, onPreview, onApply, onDelete }) {
  const color = template.color || "#0281FF";
  const isSystem = !!template.is_system;
  return (
    <article
      data-testid={`template-card-${template.id}`}
      className="group relative rounded-xl border border-border bg-card p-5 hover:border-primary/40 transition-all duration-200 overflow-hidden"
      style={{ borderTop: `3px solid ${color}` }}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center text-2xl shrink-0"
            style={{
              backgroundColor: `${color}1A`,
              border: `1px solid ${color}33`,
            }}
          >
            <span>{template.icon || "✅"}</span>
          </div>
          <div className="min-w-0">
            <h3 className="font-display font-bold text-base leading-tight truncate">
              {template.name}
            </h3>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
              {template.industry} · {template.category}
            </div>
          </div>
        </div>
        {!isSystem && (
          <button
            type="button"
            data-testid={`template-card-delete-${template.id}`}
            onClick={() => onDelete(template)}
            className="opacity-0 group-hover:opacity-100 p-1.5 rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition"
            aria-label="Delete template"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>

      <p className="text-xs text-muted-foreground line-clamp-2 mb-4 min-h-[2.5em]">
        {template.description}
      </p>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <ListChecks size={12} /> {template.task_count} tasks
          </span>
          {isSystem ? (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
              style={{ color, backgroundColor: `${color}1A` }}
            >
              System
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-secondary">
              Yours
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            data-testid={`template-card-preview-${template.id}`}
            onClick={() => onPreview(template)}
            className="px-2.5 py-1.5 text-xs rounded-md border border-border hover:bg-secondary"
          >
            Preview
          </button>
          <button
            type="button"
            data-testid={`template-card-apply-${template.id}`}
            onClick={() => onApply(template)}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md font-medium text-white hover:opacity-90"
            style={{ backgroundColor: color }}
          >
            <Wand2 size={12} strokeWidth={2.2} /> Apply
            <ChevronRight size={12} strokeWidth={2.2} />
          </button>
        </div>
      </div>
    </article>
  );
}
